import type { WeatherData } from '../types';

export const fetchWeather = async (lat: number, lon: number) => {
  try {
    const response = await fetch(
      `https://api.open-meteo.com/v1/forecast?` + 
      `latitude=${lat}&longitude=${lon}` +
      `&hourly=temperature_2m,apparent_temperature,relative_humidity_2m,precipitation_probability,weather_code,wind_speed_10m,wind_direction_10m` +
      `&daily=temperature_2m_max,temperature_2m_min,apparent_temperature_max,apparent_temperature_min,precipitation_probability_max,weather_code,wind_speed_10m_max,wind_direction_10m_dominant` +
      `&current=temperature_2m,apparent_temperature,weather_code` +
      `&timezone=auto` +
      `&forecast_days=7`
    );
    
    if (!response.ok) throw new Error('שגיאה בקבלת נתוני מזג האוויר');
    return response.json();
  } catch (error) {
    throw new Error('לא ניתן להתחבר לשירות מזג האוויר');
  }
};

// Helper function to get relevant weather data for a specific time
const getTimeRelevantData = (weatherData: WeatherData, selectedDate: Date) => {
  const now = new Date();
  const isToday = selectedDate.toDateString() === now.toDateString();
  
  if (!isToday) {
    // For future dates, return daily data
    const dayIndex = Math.floor((selectedDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return {
      weatherCode: weatherData.daily.weather_code[dayIndex],
      precipProb: weatherData.daily.precipitation_probability_max[dayIndex]
    };
  }

  // For today, find the relevant hourly data
  const currentHour = now.getHours();
  const relevantHourIndex = weatherData.hourly.time.findIndex(time => {
    const hour = new Date(time).getHours();
    return hour >= currentHour;
  });

  if (relevantHourIndex === -1) {
    // If no future hours found, use current data
    return {
      weatherCode: weatherData.current.weather_code,
      precipProb: 0 // Current precipitation probability not available in API
    };
  }

  return {
    weatherCode: weatherData.hourly.weather_code[relevantHourIndex],
    precipProb: weatherData.hourly.precipitation_probability[relevantHourIndex]
  };
};

export const getClothingLayers = (temperature: number, sensitivity: 'default' | 'cold' | 'hot' = 'default') => {
  // Adjust temperature based on sensitivity
  const adjustedTemp = sensitivity === 'cold' ? temperature - 3 : 
                      sensitivity === 'hot' ? temperature + 3 : 
                      temperature;

  if (adjustedTemp < 8) {
    return {
      layers: 3,
      emoji: "🧥❄️",
      text: "מזג אוויר קר מאוד! מומלץ ללבוש:",
      items: [
        "👕 חולצה תרמית חמה",
        "🧶 סוודר צמר עבה",
        "🧥 מעיל חורף עבה",
        "🧣 צעיף עבה, כובע צמר וכפפות",
        "🧦 גרביים תרמיות",
        "👖 מכנסיים ארוכים מבודדים"
      ]
    };
  }
  
  if (adjustedTemp < 12) {
    return {
      layers: 3,
      emoji: "🧥",
      text: "מזג אוויר קר! מומלץ ללבוש:",
      items: [
        "👕 חולצה תרמית דקה",
        "🧶 סוודר חם",
        "🧥 מעיל חורף",
        "🧣 צעיף וכובע",
        "🧦 גרביים חמות",
        "👖 מכנסיים ארוכים"
      ]
    };
  }
  
  if (adjustedTemp < 15) {
    return {
      layers: 2,
      emoji: "🧥",
      text: "מזג אוויר קריר! מומלץ ללבוש:",
      items: [
        "👕 חולצה ארוכה",
        "🧥 מעיל קל",
        "🧣 צעיף קל",
        "👖 מכנסיים ארוכים"
      ]
    };
  }
  
  if (adjustedTemp < 18) {
    return {
      layers: 2,
      emoji: "🧥",
      text: "מזג אוויר קריר-נעים. מומלץ ללבוש:",
      items: [
        "👕 חולצה ארוכה",
        "🧥 סווטשירט קל",
        "👖 מכנסיים ארוכים"
      ]
    };
  }
  
  if (adjustedTemp < 22) {
    return {
      layers: 1,
      emoji: "👕",
      text: "מזג אוויר נעים! מומלץ ללבוש:",
      items: [
        "👕 חולצה ארוכה",
        "👖 מכנסיים ארוכים",
        "🧥 קחו שכבה נוספת למקרה שיתקרר"
      ]
    };
  }
  
  if (adjustedTemp < 25) {
    return {
      layers: 1,
      emoji: "👕",
      text: "מזג אוויר נעים-חמים! מומלץ ללבוש:",
      items: [
        "👕 חולצה קצרה מבד נושם",
        "👖 מכנסיים ארוכים קלים",
        "🧢 כובע שמש לשעות החמות"
      ]
    };
  }
  
  if (adjustedTemp < 28) {
    return {
      layers: 1,
      emoji: "👕☀️",
      text: "מזג אוויר חם! מומלץ ללבוש:",
      items: [
        "👕 חולצה קצרה מבד נושם",
        "🩳 מכנסיים קצרים",
        "🧢 כובע שמש",
        "🧴 מומלץ למרוח קרם הגנה"
      ]
    };
  }
  
  if (adjustedTemp < 32) {
    return {
      layers: 1,
      emoji: "👕☀️",
      text: "מזג אוויר חם מאוד! מומלץ ללבוש:",
      items: [
        "👕 חולצה קצרה מבד דק ונושם",
        "🩳 מכנסיים קצרים קלילים",
        "🧢 כובע שמש",
        "🧴 חובה למרוח קרם הגנה",
        "💧 קחו בקבוק מים"
      ]
    };
  }
  
  return {
    layers: 1,
    emoji: "👕🌡️",
    text: "מזג אוויר חם קיצוני! מומלץ ללבוש:",
    items: [
      "👕 חולצה קצרה מבד דק ונושם בצבע בהיר",
      "🩳 מכנסיים קצרים וקלילים",
      "🧢 כובע שמש רחב שוליים",
      "🧴 חובה למרוח קרם הגנה עם מקדם גבוה",
      "💧 חובה לקחת בקבוק מים",
      "☂️ מומלץ להצטייד בשמשייה"
    ]
  };
};

export const getWindDescription = (speed: number): string => {
  if (speed < 5) return 'רוח חלשה';
  if (speed < 12) return 'רוח קלה';
  if (speed < 20) return 'רוח מתונה';
  if (speed < 30) return 'רוח חזקה';
  return 'רוח חזקה מאוד';
};

export const getWindDirection = (degrees: number): string => {
  const directions = ['צפון', 'צפון-מזרח', 'מזרח', 'דרום-מזרח', 'דרום', 'דרום-מערב', 'מערב', 'צפון-מערב'];
  const index = Math.round(degrees / 45) % 8;
  return directions[index];
};

export const getCurrentPosition = (): Promise<GeolocationPosition> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('הדפדפן שלך לא תומך באיתור מיקום'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position => resolve(position),
      error => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            reject(new Error('אנא אפשר גישה למיקום שלך'));
            break;
          case error.POSITION_UNAVAILABLE:
            reject(new Error('מידע המיקום אינו זמין'));
            break;
          case error.TIMEOUT:
            reject(new Error('בקשת המיקום נכשלה בגלל timeout'));
            break;
          default:
            reject(new Error('שגיאה לא צפויה באיתור המיקום'));
        }
      }
    );
  });
};

export const getWeatherEmoji = (weatherCode: number): string => {
  switch (weatherCode) {
    case 0: return '☀️'; // Clear sky
    case 1: return '🌤️'; // Mainly clear
    case 2: return '⛅'; // Partly cloudy
    case 3: return '☁️'; // Overcast
    case 45:
    case 48: return '🌫️'; // Foggy
    case 51:
    case 53:
    case 55: return '🌧️'; // Drizzle
    case 61:
    case 63:
    case 65: return '🌧️'; // Rain
    case 71:
    case 73:
    case 75: return '🌨️'; // Snow
    case 77: return '🌨️'; // Snow grains
    case 80:
    case 81:
    case 82: return '🌦️'; // Rain showers
    case 85:
    case 86: return '🌨️'; // Snow showers
    case 95: return '⛈️'; // Thunderstorm
    case 96:
    case 99: return '⛈️'; // Thunderstorm with hail
    default: return '🌡️';
  }
};

export const getLocationName = async (lat: number, lon: number): Promise<string> => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?` +
      `lat=${lat}&lon=${lon}&format=json&accept-language=he`
    );
    
    if (!response.ok) throw new Error();
    
    const data = await response.json();
    
    // Try to get the most relevant name
    const city = data.address.city || 
                data.address.town || 
                data.address.village || 
                data.address.suburb ||
                data.address.neighbourhood ||
                data.address.county ||
                'מיקום לא ידוע';
                
    return city;
  } catch {
    return 'מיקום לא ידוע';
  }
};

export const getTimeString = (isoString: string): string => {
  const date = new Date(isoString);
  return date.toLocaleTimeString('he-IL', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false 
  });
};

export { getTimeRelevantData };